import { getErrorMessage } from "@/lib/error-message";
import { acceptOffer, cancelOffer } from "./api";
import { requiresExternalSignature } from "./external-signing";
import { refreshQuietly } from "./place-offer";
import type { FinanceStepResponse } from "./types";

/**
 * The accept/cancel-offer submit sequence, as a plain function.
 *
 * It lives outside the dialog for the same reason the eligibility predicates
 * do: the rules that decide what request goes out, what is refetched
 * afterwards and which message the user reads are the ones the offer bug kept
 * breaking, and this repo's test runner has no DOM to exercise them in.
 *
 * The contract it enforces:
 *  - exactly ONE POST, with no body, to the offer's own id;
 *  - the refetch runs only after the backend confirmed, never optimistically —
 *    and a `*_REQUIRED` step is NOT confirmation: for an external wallet the
 *    offer is untouched until the wallet's transaction is confirmed, so
 *    `sign_required` returns without refetching and without a success message;
 *  - AND THE REFETCH CANNOT VOTE ON THE OUTCOME. It used to be awaited inside
 *    this try, so a failing offers-list request was caught here and reported
 *    as the accept/cancel itself failing — for an action the backend had
 *    already carried out. Same defect, same feature, same week as the
 *    place-offer one `place-offer.ts` documents; fixed the same way.
 *  - the message is the backend's own (`message`, or `errors[0]` on a 400) and
 *    falls back to ours only when it sent none.
 */
export type OfferDecisionMode = "accept" | "cancel";

export type OfferDecisionOutcome = {
  outcome: "success" | "failure" | "sign_required";
  /** Already resolved for display — the server's wording when it sent any. */
  message: string;
  /**
   * The raw envelope, for the one caller that needs more than a message: the
   * external-wallet handoff reads `sign_payload` and `offer_id` off it.
   * Absent on failure.
   */
  response?: FinanceStepResponse;
};

export type SubmitOfferDecisionInput = {
  mode: OfferDecisionMode;
  contractAddress: string;
  tokenId: string | number;
  offerId: number | string;
  /** Refetches the offer list (and, for accept, the NFT detail). */
  onConfirmed: () => Promise<void> | void;
  fallbackSuccessMessage: string;
  fallbackFailureMessage: string;
  /** Injection seam for tests only; production uses the real api.ts calls. */
  api?: { accept: typeof acceptOffer; cancel: typeof cancelOffer };
};

export async function submitOfferDecision({
  mode,
  contractAddress,
  tokenId,
  offerId,
  onConfirmed,
  fallbackSuccessMessage,
  fallbackFailureMessage,
  api = { accept: acceptOffer, cancel: cancelOffer },
}: SubmitOfferDecisionInput): Promise<OfferDecisionOutcome> {
  try {
    const response =
      mode === "accept"
        ? await api.accept(contractAddress, tokenId, offerId)
        : await api.cancel(contractAddress, tokenId, offerId);

    if (requiresExternalSignature(response)) {
      return { outcome: "sign_required", message: response?.message || "", response };
    }

    // Only after the backend confirmed. The new statuses (`accepted`, and
    // `obsolete` on the NFT's other offers) come from the refetch, never from
    // a locally faked update.
    //
    // Quietly: the server has already acted. If the list will not reload, the
    // user should see a stale list, not be told their action failed.
    await refreshQuietly(onConfirmed);

    return {
      outcome: "success",
      message: response?.message || fallbackSuccessMessage,
      response,
    };
  } catch (error) {
    return { outcome: "failure", message: getErrorMessage(error, fallbackFailureMessage) };
  }
}
